import React, { useEffect, useRef, useState } from 'react';
import StatBox from '../ui/StatBox';
import Confetti from '../ui/Confetti';
import { GameIntro, GameOver, GameState, LevelPicker, isTypingKey, useLevelProgress, useSaveGameResult } from './GameShell';
import { playCorrectSound, playWrongSound, playMissSound, playPopSound, playGameOverSound, playSuccessSound } from '../../utils/audio';
import { User } from '../../types';
import { ScorePops, useScorePops } from '../ui/ScorePops';

const HEIGHT = 400;
const HAZARD_Y = 352;
const AVATAR_H = 64;
const JUMP_MS = 450;
const FALL_MS = 900;
const PER_STAGE = 8; // platforms between checkpoints
const STAGES = 3; // checkpoints per world; the last one is the finish

interface World {
  name: string; icon: string; sky: [string, string, string]; hazard: [string, string]; hazardName: string;
  platforms: string[]; creatures: string[]; flyers: string[]; words: string[];
  stars?: boolean; // night sky with stars instead of clouds
}

// Each world has its own words, colors, hazard and creatures.
const WORLDS: World[] = [
  {
    name: 'Candy Land', icon: '🍭', sky: ['#f9a8d4', '#fbcfe8', '#fdf2f8'], hazard: ['#92400e', '#451a03'], hazardName: 'chocolate river',
    platforms: ['#f472b6', '#a78bfa', '#60a5fa', '#34d399', '#fbbf24', '#fb7185'], creatures: ['🍫'], flyers: ['🍬', '🍭', '🧁'],
    words: ['cake', 'pie', 'gum', 'jam', 'bun', 'tart', 'mint', 'candy', 'sweet', 'fudge', 'donut', 'cookie', 'sugar', 'honey', 'treat', 'toffee', 'cupcake', 'lollipop', 'pudding', 'waffle', 'cherry', 'muffin', 'jelly', 'cocoa', 'icing', 'yummy', 'peach', 'berry'],
  },
  {
    name: 'Jungle', icon: '🌴', sky: ['#4ade80', '#bbf7d0', '#f0fdf4'], hazard: ['#4d7c0f', '#1a2e05'], hazardName: 'swamp',
    platforms: ['#65a30d', '#a16207', '#16a34a', '#ca8a04', '#84cc16'], creatures: ['🐊'], flyers: ['🦜', '🐒', '🦋'],
    words: ['vine', 'frog', 'tiger', 'monkey', 'parrot', 'jungle', 'banana', 'leaf', 'river', 'snake', 'sloth', 'jaguar', 'toucan', 'gorilla', 'canopy', 'explore', 'mango', 'coconut', 'beetle', 'lizard', 'orchid', 'hippo', 'zebra', 'lion', 'safari', 'swing', 'bamboo', 'panda'],
  },
  {
    name: 'Ocean', icon: '🌊', sky: ['#38bdf8', '#7dd3fc', '#e0f2fe'], hazard: ['#0284c7', '#0c4a6e'], hazardName: 'shark sea',
    platforms: ['#f97316', '#facc15', '#fb7185', '#2dd4bf', '#f8fafc'], creatures: ['🦈', '🐙'], flyers: ['🐦', '☁️'],
    words: ['wave', 'fish', 'crab', 'shell', 'coral', 'whale', 'shark', 'pirate', 'anchor', 'island', 'dolphin', 'octopus', 'seaweed', 'treasure', 'sailor', 'ocean', 'turtle', 'starfish', 'jellyfish', 'harbor', 'compass', 'captain', 'current', 'splash', 'surf', 'reef', 'pearl', 'tide'],
  },
  {
    name: 'Space', icon: '🚀', sky: ['#0f172a', '#1e1b4b', '#312e81'], hazard: ['#1e1b4b', '#000000'], hazardName: 'black hole',
    platforms: ['#22d3ee', '#a855f7', '#f472b6', '#4ade80'], creatures: ['🌀'], stars: true, flyers: ['🛸', '🪐', '👾', '☄️'],
    words: ['moon', 'star', 'comet', 'orbit', 'rocket', 'planet', 'galaxy', 'meteor', 'alien', 'astronaut', 'gravity', 'nebula', 'saturn', 'jupiter', 'mars', 'venus', 'satellite', 'launch', 'cosmic', 'telescope', 'asteroid', 'universe', 'spaceship', 'eclipse', 'crater', 'beam', 'lunar', 'solar'],
  },
  {
    name: 'Volcano', icon: '🌋', sky: ['#7c2d12', '#ea580c', '#fed7aa'], hazard: ['#f97316', '#dc2626'], hazardName: 'lava',
    platforms: ['#44403c', '#57534e', '#78716c', '#292524'], creatures: ['🔥'], flyers: ['🐉', '🦅'],
    words: ['volcano', 'eruption', 'obsidian', 'dragon', 'phoenix', 'furnace', 'molten', 'magma', 'blazing', 'inferno', 'ember', 'cinder', 'scorching', 'fireball', 'courage', 'champion', 'legendary', 'adventure', 'victory', 'fearless', 'determined', 'unstoppable', 'brilliant', 'marvelous'],
  },
  // Worlds 6-10. New worlds go at the end: a student's saved level is an index into this list.
  {
    name: 'Ice Kingdom', icon: '❄️', sky: ['#7dd3fc', '#e0f2fe', '#f8fafc'], hazard: ['#38bdf8', '#075985'], hazardName: 'freezing water',
    platforms: ['#0ea5e9', '#6366f1', '#06b6d4', '#3b82f6', '#8b5cf6'], creatures: ['🐳', '🐧'], flyers: ['❄️', '🦉', '❄️'],
    words: ['frost', 'polar', 'igloo', 'husky', 'icicle', 'mitten', 'frozen', 'winter', 'chilly', 'arctic', 'aurora', 'walrus', 'penguin', 'glacier', 'iceberg', 'skating', 'crystal', 'shimmer', 'reindeer', 'blizzard', 'snowball', 'sledding', 'northern', 'toboggan', 'snowflake', 'snowstorm', 'avalanche', 'fireplace'],
  },
  {
    name: 'Haunted Mansion', icon: '👻', sky: ['#1e1b4b', '#4c1d95', '#7c3aed'], hazard: ['#6d28d9', '#1e1b4b'], hazardName: 'ghostly mist',
    platforms: ['#78716c', '#a16207', '#57534e', '#7c2d12'], creatures: ['👻'], flyers: ['🦇', '🌕', '🦇'], stars: true,
    words: ['ghost', 'spooky', 'shadow', 'creaky', 'cobweb', 'spider', 'candle', 'goblin', 'potion', 'haunted', 'lantern', 'pumpkin', 'vampire', 'mystery', 'phantom', 'whisper', 'monster', 'costume', 'skeleton', 'werewolf', 'midnight', 'cauldron', 'portrait', 'moonlight', 'enchanted', 'graveyard', 'broomstick', 'chandelier'],
  },
  {
    name: 'Dino Valley', icon: '🦖', sky: ['#fbbf24', '#fde68a', '#fffbeb'], hazard: ['#292524', '#0c0a09'], hazardName: 'tar pit',
    platforms: ['#a8a29e', '#65a30d', '#b45309', '#78716c'], creatures: ['🦴'], flyers: ['🦕', '🦖', '🌋'],
    words: ['fossil', 'raptor', 'claws', 'swamp', 'giant', 'meteor', 'canyon', 'extinct', 'ancient', 'eggshell', 'dinosaur', 'volcano', 'jurassic', 'predator', 'skeleton', 'excavate', 'footprint', 'herbivore', 'carnivore', 'stampede', 'prehistoric', 'pterodactyl', 'triceratops', 'stegosaurus', 'brontosaurus', 'velociraptor', 'tyrannosaurus'],
  },
  {
    name: 'Sky City', icon: '🎈', sky: ['#a78bfa', '#ddd6fe', '#fdf4ff'], hazard: ['#64748b', '#1e293b'], hazardName: 'thunderstorm',
    platforms: ['#ec4899', '#f59e0b', '#06b6d4', '#22c55e', '#f43f5e'], creatures: ['⚡'], flyers: ['🎈', '🌈', '🦅'],
    words: ['cloud', 'eagle', 'breeze', 'glider', 'stormy', 'rainbow', 'thunder', 'balloon', 'airship', 'feather', 'sunbeam', 'weather', 'tornado', 'soaring', 'floating', 'altitude', 'heavenly', 'horizon', 'windmill', 'lightning', 'propeller', 'parachute', 'whirlwind', 'skyscraper', 'helicopter', 'atmosphere'],
  },
  {
    name: 'Robot Factory', icon: '🤖', sky: ['#334155', '#64748b', '#cbd5e1'], hazard: ['#22d3ee', '#0e7490'], hazardName: 'electric pool',
    platforms: ['#f59e0b', '#94a3b8', '#ef4444', '#22c55e', '#e2e8f0'], creatures: ['⚙️', '⚡'], flyers: ['🤖', '🛸', '🔧'],
    words: ['robot', 'gears', 'bolts', 'circuit', 'battery', 'machine', 'android', 'program', 'conveyor', 'computer', 'keyboard', 'engineer', 'magnetic', 'electric', 'powerful', 'invention', 'hydraulic', 'calculate', 'automatic', 'blueprint', 'processor', 'algorithm', 'microchip', 'laboratory', 'technology', 'transistor', 'unstoppable'],
  },
];

// Runners: the first two are free, beating world N unlocks the next.
export const OUTFITS = [
  { id: 'classic', label: 'Classic', head: '#facc15', torso: '#2563eb', legs: '#22c55e', arms: '#facc15', hat: '' },
  { id: 'ninja', label: 'Ninja', head: '#1f2937', torso: '#111827', legs: '#dc2626', arms: '#1f2937', hat: '' },
  { id: 'royal', label: 'Royal', head: '#fcd5b5', torso: '#ec4899', legs: '#a855f7', arms: '#fcd5b5', hat: '👑' },
  { id: 'explorer', label: 'Explorer', head: '#e0ac69', torso: '#a16207', legs: '#78350f', arms: '#e0ac69', hat: '🤠' },
  { id: 'pirate', label: 'Pirate', head: '#fcd5b5', torso: '#b91c1c', legs: '#1f2937', arms: '#fcd5b5', hat: '🏴‍☠️' },
  { id: 'astronaut', label: 'Astronaut', head: '#e5e7eb', torso: '#f8fafc', legs: '#cbd5e1', arms: '#f8fafc', hat: '' },
  { id: 'robot', label: 'Robot', head: '#9ca3af', torso: '#06b6d4', legs: '#475569', arms: '#9ca3af', hat: '📡' },
  { id: 'knight', label: 'Knight', head: '#9ca3af', torso: '#64748b', legs: '#475569', arms: '#9ca3af', hat: '🛡️' },
  { id: 'magician', label: 'Magician', head: '#fcd5b5', torso: '#4c1d95', legs: '#312e81', arms: '#fcd5b5', hat: '🎩' },
  { id: 'dino', label: 'Dino', head: '#22c55e', torso: '#16a34a', legs: '#15803d', arms: '#22c55e', hat: '🦕' },
  { id: 'hero', label: 'Superhero', head: '#fcd5b5', torso: '#dc2626', legs: '#1d4ed8', arms: '#dc2626', hat: '⭐' },
  { id: 'golden', label: 'Golden Legend', head: '#facc15', torso: '#eab308', legs: '#ca8a04', arms: '#facc15', hat: '✨' },
];
type Outfit = typeof OUTFITS[number];
const outfitsUnlocked = (unlockedWorld: number) => 2 + unlockedWorld;

interface Platform { id: number; x: number; y: number; w: number; color: string; word: string; typed: number; missed: boolean; coin: boolean; checkpoint: boolean }

const Avatar: React.FC<{ o: Outfit; running?: boolean }> = ({ o, running }) => (
  <div className="relative" style={{ width: 30, height: AVATAR_H }}>
    {o.hat && <span className="absolute text-lg leading-none" style={{ left: 5, top: -16 }}>{o.hat}</span>}
    <div className="absolute rounded-[4px]" style={{ left: 4, top: 0, width: 22, height: 22, background: o.head, border: '2px solid rgba(0,0,0,0.25)' }}>
      {o.id === 'ninja' && <div className="absolute inset-x-0" style={{ top: 6, height: 7, background: '#fcd5b5' }} />}
      {o.id === 'astronaut' && <div className="absolute rounded-sm" style={{ left: 2, top: 4, width: 15, height: 10, background: '#1e3a8a' }} />}
      {o.id !== 'astronaut' && <>
        <span className="absolute rounded-full" style={{ left: 5, top: 7, width: 4, height: 4, background: o.id === 'robot' ? '#22d3ee' : '#111' }} />
        <span className="absolute rounded-full" style={{ left: 12, top: 7, width: 4, height: 4, background: o.id === 'robot' ? '#22d3ee' : '#111' }} />
      </>}
      {o.id !== 'ninja' && o.id !== 'astronaut' && <span className="absolute" style={{ left: 5, top: 12, width: 10, height: 4, borderBottom: '2px solid #111', borderRadius: '0 0 8px 8px' }} />}
    </div>
    <div className={`absolute ${running ? 'ob-arm-a' : ''}`} style={{ left: -6, top: 23, width: 7, height: 20, background: o.arms, border: '1px solid rgba(0,0,0,0.25)' }} />
    <div className={`absolute ${running ? 'ob-arm-b' : ''}`} style={{ left: 29, top: 23, width: 7, height: 20, background: o.arms, border: '1px solid rgba(0,0,0,0.25)' }} />
    <div className="absolute" style={{ left: 0, top: 22, width: 30, height: 22, background: o.torso, border: '2px solid rgba(0,0,0,0.2)' }} />
    <div className="absolute" style={{ left: 1, top: 44, width: 13, height: 20, background: o.legs, border: '2px solid rgba(0,0,0,0.2)' }} />
    <div className="absolute" style={{ left: 16, top: 44, width: 13, height: 20, background: o.legs, border: '2px solid rgba(0,0,0,0.2)' }} />
  </div>
);

const stageOf = (idx: number) => Math.min(STAGES, Math.floor(idx / PER_STAGE) + 1);
const FINISH = PER_STAGE * STAGES;

const newState = (world: number) => ({
  world, platforms: [] as Platform[], current: 0, checkpoint: 0, stage: 1, lives: 3, coins: 0, score: 0,
  jumps: 0, correct: 0, mistakes: 0, fuse: 0, fuseMax: 1, waiting: true, camX: 0, won: false, newUnlock: false,
  jump: null as { from: number; to: number; at: number } | null,
  fall: null as { x: number; y: number; at: number } | null,
  celebrate: 0, shakeAt: 0,
});
type S = ReturnType<typeof newState>;

const ObbyRunGame: React.FC<{ user?: User }> = ({ user }) => {
  const [gameState, setGameState] = useState<GameState>('ready');
  const [, setFrame] = useState(0);
  // unlocked === WORLDS.length means every world has been beaten.
  const [unlocked, unlock, levelsLoaded] = useLevelProgress('obby-run', user);
  const [world, setWorld] = useState(() => Math.min(unlocked, WORLDS.length - 1));
  useEffect(() => { if (levelsLoaded) setWorld(Math.min(unlocked, WORLDS.length - 1)); }, [levelsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const [outfit, setOutfit] = useState<Outfit>(OUTFITS[0]);
  const areaRef = useRef<HTMLDivElement>(null);
  const { pops, pop } = useScorePops();
  const g = useRef(newState(0));
  const W = WORLDS[g.current.world];

  const pickWord = (s: S, idx: number, prev?: string) => {
    const words = WORLDS[s.world].words;
    const min = Math.min(...words.map(w => w.length)) + stageOf(idx) - 1;
    const pool = words.filter(w => w.length >= min && w.length <= min + 3 && w !== prev);
    const source = pool.length >= 3 ? pool : words.filter(w => w !== prev);
    return source[Math.floor(Math.random() * source.length)];
  };

  const makePlatform = (s: S, prev: Platform | undefined, idx: number): Platform => {
    const checkpoint = idx % PER_STAGE === 0;
    const w = checkpoint ? 150 : 96 + Math.random() * 30;
    const colors = WORLDS[s.world].platforms;
    return {
      id: idx, w, checkpoint,
      x: prev ? prev.x + prev.w + 70 + Math.random() * 45 : 30,
      y: prev ? Math.max(150, Math.min(262, prev.y + (Math.random() * 2 - 1) * 70)) : 258,
      color: checkpoint ? (idx === FINISH ? '#facc15' : '#e5e7eb') : colors[idx % colors.length],
      word: idx === 0 ? '' : pickWord(s, idx, prev?.word), typed: 0, missed: false,
      coin: !checkpoint && Math.random() < 0.5,
    };
  };

  const ensurePlatforms = (s: S) => {
    const want = Math.min(FINISH + 1, s.current + 8);
    while (s.platforms.length < want) s.platforms.push(makePlatform(s, s.platforms[s.platforms.length - 1], s.platforms.length));
  };

  // Time allowed on a platform scales with the next word's length and shrinks each stage and world.
  const fuseFor = (s: S, word: string, checkpoint: boolean) =>
    (2 + word.length * Math.max(0.4, 1 - s.world * 0.06 - (s.stage - 1) * 0.05)) * (checkpoint ? 1.5 : 1);

  const startGame = (w = world) => {
    const s = newState(w);
    ensurePlatforms(s);
    s.fuseMax = fuseFor(s, s.platforms[1].word, true);
    s.fuse = s.fuseMax;
    g.current = s;
    setWorld(w);
    setGameState('playing');
  };

  useSaveGameResult(user, 'obby-run', gameState, g.current.score);

  const respawn = (s: S) => {
    s.current = s.checkpoint;
    s.platforms = s.platforms.slice(0, s.checkpoint + 1);
    ensurePlatforms(s);
    s.fall = null;
    s.waiting = true;
    s.fuseMax = fuseFor(s, s.platforms[s.current + 1].word, true);
    s.fuse = s.fuseMax;
  };

  // Game loop: jumps, falls, crumbling platforms, camera.
  useEffect(() => {
    if (gameState !== 'playing') return;
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const s = g.current;

      if (s.jump && now - s.jump.at >= JUMP_MS) {
        const landed = s.platforms[s.jump.to];
        s.jump = null;
        if (landed.id === FINISH) {
          s.won = true;
          s.score += 300 * (s.world + 1) + s.lives * 100;
          playSuccessSound();
          s.newUnlock = s.world + 1 > unlocked;
          unlock(s.world + 1);
          setGameState('over');
          return;
        }
        if (landed.checkpoint && landed.id > s.checkpoint) {
          s.checkpoint = landed.id;
          s.stage = stageOf(landed.id);
          s.score += 100 * (s.stage - 1);
          s.celebrate = now;
          pop(`Stage ${s.stage}! 🚩`, 50, 30, '#7c3aed');
          playSuccessSound();
        }
      }
      if (s.fall && now - s.fall.at >= FALL_MS) {
        s.lives--;
        if (s.lives <= 0) {
          playGameOverSound();
          setGameState('over');
          return;
        }
        respawn(s);
      }
      if (!s.jump && !s.fall && !s.waiting) {
        s.fuse -= dt;
        if (s.fuse <= 0) {
          const p = s.platforms[s.current];
          s.fall = { x: p.x + p.w / 2, y: p.y, at: now };
          playMissSound();
        }
      }

      const width = areaRef.current?.offsetWidth ?? 800;
      const p = s.platforms[s.current];
      s.camX += (p.x + p.w / 2 - width * 0.3 - s.camX) * Math.min(1, dt * 5);
      setFrame(f => f + 1);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState]);

  // Typing: the word on the next platform; finishing it jumps there.
  useEffect(() => {
    if (gameState !== 'playing') return;
    const onKey = (e: KeyboardEvent) => {
      if (!isTypingKey(e)) return;
      e.preventDefault();
      const s = g.current;
      const target = s.platforms[s.current + 1];
      if (s.fall || !target) return;
      const now = performance.now();
      s.waiting = false;
      if (e.key.toLowerCase() !== target.word[target.typed]) {
        s.mistakes++;
        s.shakeAt = now;
        if (target.coin && !target.missed) pop('coin lost', 55, 25, '#9ca3af');
        target.missed = true;
        playWrongSound();
        return;
      }
      target.typed++;
      s.correct++;
      if (target.typed < target.word.length) { playCorrectSound(); return; }

      // Jump!
      s.jump = { from: s.current, to: s.current + 1, at: now };
      s.current++;
      s.jumps++;
      const perfect = !target.missed;
      s.score += target.word.length * 10 + (perfect ? 20 : 0);
      if (target.coin && perfect) {
        s.coins++;
        s.score += 25;
        pop('+1 🪙', 55, 25, '#f59e0b');
      }
      playPopSound();
      ensurePlatforms(s);
      const next = s.platforms[s.current + 1];
      if (next) {
        s.fuseMax = fuseFor(s, next.word, target.checkpoint);
        s.fuse = s.fuseMax + JUMP_MS / 1000;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, pop]);

  const s = g.current;
  const accuracy = s.correct + s.mistakes > 0 ? Math.round((s.correct / (s.correct + s.mistakes)) * 100) : 100;
  const button = 'bg-gradient-to-r from-sky-500 to-indigo-500';
  const openOutfits = outfitsUnlocked(unlocked);

  if (gameState === 'ready') {
    return (
      <GameIntro icon="🏃" title="Obby Run" skill="steady, accurate typing -- each word is a jump." accent="text-sky-600" button={button} onStart={() => startGame()}>
        <p>Type the word on the next platform to jump to it. Don't wait too long -- platforms crumble! Type a word with no mistakes to grab its coin. Reach the 🏁 after {STAGES} stages to beat the world and unlock a new runner. You have 3 lives.</p>
        <p className="mt-4 font-bold text-gray-700">Choose a world:</p>
        <LevelPicker levels={WORLDS} unlocked={unlocked} selected={world} onSelect={setWorld} />
        <p className="mt-4 font-bold text-gray-700">Pick your runner:</p>
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          {OUTFITS.map((o, i) => {
            const locked = i >= openOutfits;
            return (
              <button key={o.id} type="button" disabled={locked} onClick={() => setOutfit(o)} aria-pressed={outfit.id === o.id}
                title={locked ? `Beat World ${i - 1} to unlock` : o.label}
                className={`relative flex flex-col items-center gap-1 px-3 pt-5 pb-2 rounded-xl bg-white shadow transition-transform
                  ${locked ? 'opacity-40 grayscale cursor-not-allowed' : 'hover:scale-105'} ${outfit.id === o.id ? 'ring-4 ring-sky-400' : 'ring-1 ring-gray-200'}`}>
                <Avatar o={o} />
                <span className="text-xs font-bold text-gray-700">{locked ? `🔒 World ${i - 1}` : o.label}</span>
              </button>
            );
          })}
        </div>
      </GameIntro>
    );
  }
  if (gameState === 'over') {
    const next = s.world + 1 < WORLDS.length ? WORLDS[s.world + 1] : null;
    const newRunner = s.won && s.newUnlock ? OUTFITS[2 + s.world] : null;
    return (
      <GameOver
        title={s.won ? `${W.name} Complete!` : 'Obby Over!'}
        message={s.won ? (next ? `World ${s.world + 2}: ${next.name} ${next.icon} is unlocked!` : 'You beat every world -- parkour legend! 🏆') : `You reached stage ${s.stage} of ${STAGES}. Jump back in!`}
        stars={s.won ? s.lives : s.jumps > 0 ? 1 : 0}
        stats={[{ label: 'Score', value: s.score }, { label: 'Stage', value: `${s.stage}/${STAGES}` }, { label: 'Coins', value: s.coins }, { label: 'Jumps', value: s.jumps }, { label: 'Accuracy', value: `${accuracy}%` }]}
        button={button}
        onRestart={() => startGame(s.world)}
      >
        {newRunner && (
          <div className="mt-4 inline-flex items-center gap-3 px-4 pt-5 pb-3 rounded-2xl bg-white shadow ring-2 ring-yellow-300">
            <Avatar o={newRunner} />
            <span className="font-black text-gray-700">New runner unlocked: {newRunner.label}!</span>
          </div>
        )}
        {s.won && next && (
          <div>
            <button onClick={() => startGame(s.world + 1)} className="fun-shine mt-6 px-8 py-3 text-white rounded-full font-black text-xl shadow-lg ring-4 ring-white bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-transform hover:scale-105 active:scale-95">
              Next World: {next.name} {next.icon} ▶
            </button>
          </div>
        )}
      </GameOver>
    );
  }

  const now = performance.now();
  const cam = s.camX;
  const width = areaRef.current?.offsetWidth ?? 800;
  const cur = s.platforms[s.current];
  let ax = cur.x + cur.w / 2;
  let ay = cur.y;
  let spin = 0;
  if (s.jump) {
    const a = s.platforms[s.jump.from], b = s.platforms[s.jump.to];
    const t = Math.min(1, (now - s.jump.at) / JUMP_MS);
    ax = a.x + a.w / 2 + (b.x + b.w / 2 - a.x - a.w / 2) * t;
    ay = a.y + (b.y - a.y) * t - Math.sin(Math.PI * t) * 70;
  } else if (s.fall) {
    const t = (now - s.fall.at) / 1000;
    ax = s.fall.x + t * 40;
    ay = s.fall.y + 500 * t * t;
    spin = t * 400;
  }
  const fusePct = Math.max(0, Math.min(1, s.fuse / s.fuseMax));
  const crumbling = !s.jump && !s.fall && !s.waiting && fusePct < 0.3;
  const shaking = now - s.shakeAt < 300;
  const dark = !!W.stars;

  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-around items-center mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
        <StatBox label="Score" value={s.score} />
        <StatBox label="World" value={`${W.icon} ${s.world + 1}`} />
        <StatBox label="Stage" value={`${s.stage}/${STAGES}`} />
        <StatBox label="Coins" value={s.coins} />
        <div className="flex flex-col items-center px-4 py-2 min-w-[80px] rounded-2xl bg-white shadow-md ring-2 ring-white/70">
          <span className="text-[11px] font-extrabold uppercase tracking-wider text-rose-500">Lives</span>
          <span className="text-2xl tracking-wider" aria-label={`${s.lives} ${s.lives === 1 ? 'life' : 'lives'} left`}>
            {'❤️'.repeat(Math.max(0, s.lives))}<span className="opacity-20">{'🖤'.repeat(Math.max(0, 3 - s.lives))}</span>
          </span>
        </div>
      </div>

      <div ref={areaRef} className={`relative overflow-hidden rounded-2xl border-4 border-white shadow-inner select-none ${shaking ? 'shake' : ''}`}
        style={{ height: HEIGHT, background: `linear-gradient(${W.sky[0]}, ${W.sky[1]}, ${W.sky[2]})` }}>
        {s.celebrate > 0 && now - s.celebrate < 4500 && <Confetti key={s.celebrate} />}
        {/* Stars in space, clouds elsewhere (parallax) */}
        {dark
          ? Array.from({ length: 40 }, (_, i) => {
            const x = (((i * 137) % 1400) - cam * 0.15) % 1400;
            return <span key={i} className="absolute rounded-full bg-white" style={{ left: (x + 1400) % 1400 - 100, top: (i * 53) % 330, width: i % 5 ? 2 : 3, height: i % 5 ? 2 : 3, opacity: 0.8 }} />;
          })
          : [0, 1, 2, 3, 4].map(i => {
            const span = 1400;
            const x = ((i * 300 - cam * 0.3) % span + span) % span - 150;
            return <div key={i} className="absolute bg-white/80 rounded-sm" style={{ left: x, top: 30 + (i % 3) * 38, width: 80 + (i % 2) * 50, height: 24 }} aria-hidden="true" />;
          })}
        {/* Flying creatures of this world */}
        {W.flyers.map((f, i) => {
          const span = width + 300;
          const x = ((i * 420 + 200 - cam * 0.5) % span + span) % span - 120;
          return <span key={i} className="absolute text-3xl fun-float opacity-80" style={{ left: x, top: 40 + i * 45, animationDelay: `${i * 1.3}s` }} aria-hidden="true">{f}</span>;
        })}

        {/* Platforms */}
        {s.platforms.map((p, i) => {
          const left = p.x - cam;
          if (left > 2000 || left + p.w < -200) return null;
          const isNext = i === s.current + 1;
          const isCurrent = i === s.current;
          const passed = i <= s.current;
          return (
            <React.Fragment key={p.id}>
              <div className={`absolute ${isCurrent && crumbling ? 'ob-wobble' : ''}`}
                style={{
                  left, top: p.y, width: p.w, height: 26,
                  background: p.color,
                  backgroundImage: 'radial-gradient(circle at 50% 35%, rgba(255,255,255,0.45) 3px, transparent 4px)',
                  backgroundSize: '16px 26px',
                  borderBottom: '7px solid rgba(0,0,0,0.25)',
                  opacity: isCurrent && !s.waiting && !s.jump ? 0.45 + 0.55 * fusePct : 1,
                }}>
                {p.checkpoint && p.id > 0 && <span className="absolute -top-9 right-3 text-3xl" aria-hidden="true">{p.id === FINISH ? '🏁' : passed ? '✅' : '🚩'}</span>}
              </div>
              {p.coin && !passed && !p.missed && <span className="absolute text-2xl ob-coin" style={{ left: left + p.w / 2 - 12, top: p.y - 46 }} aria-hidden="true">🪙</span>}
              {!passed && p.word && (
                <span className={`absolute -translate-x-1/2 px-2 py-0.5 rounded-lg font-mono font-bold border-2 shadow-md whitespace-nowrap transition-transform
                  ${isNext ? 'text-xl md:text-2xl bg-white border-sky-400 scale-110 z-10' : 'text-sm bg-white/70 border-white/60 text-gray-500'}`}
                  style={{ left: left + p.w / 2, top: p.y + 40 }}>
                  {isNext ? <><span className="text-green-600">{p.word.slice(0, p.typed)}</span><span className="text-sky-700">{p.word.slice(p.typed)}</span></> : p.word}
                </span>
              )}
              {isCurrent && !s.jump && !s.fall && !s.waiting && (
                <div className="absolute h-2 rounded-full bg-black/20 overflow-hidden" style={{ left, top: p.y + 30, width: p.w }}>
                  <div className={`h-full ${fusePct < 0.3 ? 'bg-red-500' : fusePct < 0.6 ? 'bg-amber-400' : 'bg-green-500'}`} style={{ width: `${fusePct * 100}%` }} />
                </div>
              )}
            </React.Fragment>
          );
        })}

        {/* Avatar */}
        <div className="absolute z-20" style={{ left: ax - 15 - cam, top: ay - AVATAR_H, transform: spin ? `rotate(${spin}deg)` : undefined }}>
          <Avatar o={outfit} running={!!s.jump} />
        </div>

        {/* Hazard with its creatures */}
        <div className="absolute inset-x-0 bottom-0 z-10" style={{ top: HAZARD_Y, background: `linear-gradient(${W.hazard[0]}, ${W.hazard[1]})` }}>
          <div className="absolute inset-x-0 top-0 h-2 bg-white/30" />
          {W.creatures.map((c, i) => {
            const span = width + 200;
            const x = ((i * 380 + now * 0.05 - cam * 0.2) % span + span) % span - 60;
            return <span key={i} className="absolute text-3xl" style={{ left: x, top: -14 + (i % 2) * 8 }} aria-hidden="true">{c}</span>;
          })}
          {[8, 27, 46, 63, 81].map((x, i) => (
            <span key={i} className="absolute rounded-sm bg-white/40 ob-bubble" style={{ left: `${x}%`, bottom: 6, width: 10, height: 10, animationDelay: `${i * 0.7}s` }} />
          ))}
        </div>

        {s.waiting && (
          <div className="absolute left-1/2 -translate-x-1/2 top-6 z-30 px-4 py-1.5 rounded-full bg-white/90 text-sky-700 font-black shadow fun-bounce whitespace-nowrap">
            {s.jumps === 0 ? `World ${s.world + 1}: ${W.name} ${W.icon} -- type to jump!` : 'Respawned! Type to go 🚀'}
          </div>
        )}
        <ScorePops pops={pops} />
        <style>{`
          .ob-wobble { animation: ob-wobble 0.15s linear infinite; }
          @keyframes ob-wobble { 0%, 100% { transform: translate(0, 0); } 25% { transform: translate(-2px, 1px); } 75% { transform: translate(2px, 1px); } }
          .ob-coin { animation: ob-coin 1.2s ease-in-out infinite; }
          @keyframes ob-coin { 0%, 100% { transform: translateY(0) scaleX(1); } 50% { transform: translateY(-6px) scaleX(0.6); } }
          .ob-bubble { animation: ob-bubble 2.4s ease-in infinite; }
          @keyframes ob-bubble { 0% { transform: translateY(0); opacity: 0; } 30% { opacity: 1; } 100% { transform: translateY(-40px); opacity: 0; } }
          .ob-arm-a { animation: ob-arm 0.3s ease-in-out infinite; transform-origin: top; }
          .ob-arm-b { animation: ob-arm 0.3s ease-in-out infinite reverse; transform-origin: top; }
          @keyframes ob-arm { 0%, 100% { transform: rotate(-40deg); } 50% { transform: rotate(40deg); } }
        `}</style>
      </div>
      <p className="text-center text-sm text-gray-500 mt-3">Watch out for the {W.hazardName}! The next platform's word is the big one.</p>
    </div>
  );
};

export default ObbyRunGame;
